import { useCallback, useEffect, useEffectEvent, useId, useMemo, useRef, useState } from 'react';
import * as L from 'leaflet';
import { IServerSideRendered } from '../../Shared/Base/server-side-rendered';
import MapComponent, { IMap } from '../../Shared/Map';
import { applySnapshot, applyDelta } from './vehicle-positions-state';
import VehicleMarker from './markers/VehicleMarker';
import StopLocationMarker from './markers/StopLocationMarker';
import './style.scss';
import { LineApiModel } from './models/line.api-model';
import { StopAreaApiModel } from './models/stop-area.api-model';
import useFetch from '../../../hooks/fetch.hook';
import type { IStopArea } from '../../../models/stop-area.model';
import type { IStopPoint } from '../../../models/stop-point.model';
import { TransportMode } from '../../../models/transport-mode.enum';
import { VasttrafikGidHelper } from '../../../helpers/vasttrafik-gid.helper';
import IMultiFacet from '../../../models/multi-facet.model';
import UserLocationMarker from './markers/UserLocationMarker';
import { AnalyticsService } from '../../../services/analytics-service';
import LogService from '../../../services/log-service/log.service';
import { GeolocationService } from '../../../services/geolocation.service';
import VehicleRoutePolylines from './components/VehicleRoutePolylines';
import type { ILine } from '../../../models/line.model';
import { ICoordinate } from '../../../models/coordinate.model';
import MapSettings from './components/MapSettings';
import { t } from '../../../services/texts.service';
import MapSettingsControl from './components/MapSettingsControl';
import FeedbackControl from './components/FeedbackControl';
import JourneyPanel from './components/JourneyPanel';
import MapPanelLayout from './components/MapPanelLayout';

export type IVehiclePositionsMapTexts = { informationTextHtml: string };
export type IVehiclePositionsMapSettings = {
  streamUrl: string;
  showFeedbackControl: boolean;
};
export type IVehiclePositionsMapProps = IServerSideRendered<IVehiclePositionsMapTexts, IVehiclePositionsMapSettings>;

export interface VehiclePosition {
  vehicleId: string;
  timestamp: string;
  serviceJourneyGid: string;
  status: string;
  atStop: boolean | null;
  lastStopPointGid: string | null;
  latitude: number | null;
  longitude: number | null;
  speedKmh: number | null;
  course: number | null;
  destination: string | null;
  via: string | null;
}

export interface VehiclePositionsDelta {
  updated: VehiclePosition[];
  removed: string[];
}

interface IJourneyPatternResponse {
  coordinates: ICoordinate[];
  stopPointGids: string[];
}

interface ISelectedVehicleRouteContext {
  vehicleId: string;
  serviceJourneyGid: string;
  line: ILine;
  destination: string;
}

const BOUNDS_DEBOUNCE_MS = 300;

const LINES_URL = '/api/vehicle-positions-map/lines';
const STOP_AREAS_URL = '/api/vehicle-positions-map/stop-areas?includeStopPoints=true';
const STREAM_SUFFIX = '/stream';
const STOP_AREA_MIN_ZOOM = 14;
const LOCATE_ZOOM_LEVEL = 15; // NV-8675: initial zoom level when centering the map on the user’s position
const DEFAULT_TRANSPORT_MODES = [TransportMode.bus, TransportMode.tram, TransportMode.ferry, TransportMode.train];

export default function VehiclePositionsMap({ settings, texts }: IVehiclePositionsMapProps) {
  const filterPanelId = useId();
  const [bounds, setBounds] = useState<L.LatLngBounds | null>(null);
  const [debouncedBounds, setDebouncedBounds] = useState<L.LatLngBounds | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopAreasAbortControllerRef = useRef<AbortController | null>(null);
  const vehicleRouteAbortControllerRef = useRef<AbortController | null>(null);
  const currentPositionAbortController = useRef<AbortController | null>(null);
  const isLoadingStopAreasRef = useRef(false);
  const vehiclesRef = useRef<Map<string, VehiclePosition>>(new Map());
  const [vehicles, setVehicles] = useState<VehiclePosition[]>([]);
  const [now, setNow] = useState(Date.now);
  const [{ data }] = useFetch<LineApiModel[]>(LINES_URL);
  const mapRef = useRef<IMap>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const filterTriggerRef = useRef<HTMLElement | null>(null);
  const zoomRef = useRef<number>(13);
  const [isAboveStopAreaZoom, setIsAboveStopAreaZoom] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | undefined>(undefined);
  const [selectedModes, setSelectedModes] = useState<TransportMode[]>(DEFAULT_TRANSPORT_MODES);
  const [selectedLines, setSelectedLines] = useState<IMultiFacet[]>([]);
  const [showMapSettings, setShowMapSettings] = useState(false);
  const [userPosition, setUserPosition] = useState<[number, number] | undefined>(undefined);
  const [stopAreasData, setStopAreasData] = useState<StopAreaApiModel[] | undefined>(undefined);
  const [hasLoadedStopAreas, setHasLoadedStopAreas] = useState(false);
  const [selectedVehicleRouteCoordinates, setSelectedVehicleRouteCoordinates] = useState<ICoordinate[] | undefined>(undefined);
  const [selectedVehicleRouteStopPointGids, setSelectedVehicleRouteStopPointGids] = useState<string[]>([]);
  const [selectedVehicleRouteContext, setSelectedVehicleRouteContext] = useState<ISelectedVehicleRouteContext | undefined>(undefined);
  const [feedbackHasBeenShown, setFeedbackHasBeenShown] = useState(false);
  const [showJourneyPanel, setShowJourneyPanel] = useState(false);

  useEffect(
    () => () => {
      stopAreasAbortControllerRef.current?.abort();
      vehicleRouteAbortControllerRef.current?.abort();
    },
    [],
  );

  const userHasInteractedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    currentPositionAbortController.current = abortController;

    GeolocationService.getCurrentPosition(abortController.signal)
      .then(({ latitude, longitude }) => {
        if (!isMounted) return;

        const coords: [number, number] = [latitude, longitude];
        setUserPosition(coords);

        // Don't change map view if the user has already interacted with the map (e.g. panned or zoomed), to avoid disrupting their experience.
        if (userHasInteractedRef.current) return;

        mapRef.current?.setView(coords, LOCATE_ZOOM_LEVEL);
      })
      .catch(() => {
        // Permission denied or unavailable — keep default map view
      });

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  // === VARIABLES ===

  const hasActiveFilters = selectedModes.length !== DEFAULT_TRANSPORT_MODES.length || selectedModes.some((m) => !DEFAULT_TRANSPORT_MODES.includes(m)) || selectedLines.length > 0;

  const lines = useMemo(() => data?.map(LineApiModel.mapToBusinessModel) ?? [], [data]);
  const linesByGid = useMemo(() => new Map(lines.filter((l) => l.gid).map((l) => [l.gid!, l])), [lines]);

  const vehiclesWithLine = useMemo(
    () =>
      vehicles.map((vehicle) => {
        const lineGid = VasttrafikGidHelper.getLineGidFromServiceJourneyGid(vehicle.serviceJourneyGid);
        const line = lineGid ? linesByGid.get(lineGid) : undefined;

        return { vehicle, line };
      }),
    [vehicles, linesByGid],
  );

  const filteredVehicles = useMemo(() => {
    const noFilter = selectedModes.length === 0;
    if (noFilter) return [];

    return vehiclesWithLine.filter(({ line }) => {
      if (!line) return false;
      return selectedModes.includes(line.transportMode);
    });
  }, [vehiclesWithLine, selectedModes]);

  const selectedLineGids = useMemo(() => selectedLines.map((f) => f.line?.gid).filter((gid): gid is string => !!gid), [selectedLines]);

  const stopAreas = useMemo<IStopArea[]>(() => stopAreasData?.map(StopAreaApiModel.mapToBusinessModel) ?? [], [stopAreasData]);
  const hasSelectedVehicleStopPoints = selectedVehicleRouteStopPointGids.length > 0;

  const selectedServiceJourneyGid = selectedVehicleRouteContext?.serviceJourneyGid;

  const abortCurrentPositionLookup = useCallback(() => {
    if (!currentPositionAbortController.current?.signal.aborted) {
      currentPositionAbortController.current?.abort();
    }
  }, [currentPositionAbortController]);

  const handleVehicleSelect = useCallback(
    (vehicle: VehiclePosition, line: ILine | undefined) => {
      const next = selectedVehicleId === vehicle.vehicleId ? undefined : vehicle.vehicleId;
      setSelectedVehicleRouteCoordinates(undefined);
      setSelectedVehicleRouteStopPointGids([]);
      setSelectedVehicleId(next);
      setShowJourneyPanel(!!next && !!line);
      abortCurrentPositionLookup();

      if (!next || !line) {
        setSelectedVehicleRouteContext(undefined);
        return;
      }

      setSelectedVehicleRouteContext({
        vehicleId: vehicle.vehicleId,
        serviceJourneyGid: vehicle.serviceJourneyGid,
        line,
        destination: `${vehicle.destination ?? ''}${vehicle.via ? ` via ${vehicle.via}` : ''}`,
      });
      setShowMapSettings(false);
      document.dispatchEvent(new CustomEvent('hide-vehicle-position-map-feedback-panel'));
    },
    [abortCurrentPositionLookup, selectedVehicleId],
  );

  const vehicleMarkers = useMemo(
    () =>
      filteredVehicles.map(({ vehicle, line }) => (
        <VehicleMarker
          vehicle={vehicle}
          line={line}
          key={vehicle.vehicleId}
          isSelected={selectedVehicleId === vehicle.vehicleId}
          isDeselected={selectedVehicleId !== undefined && selectedVehicleId !== vehicle.vehicleId}
          onSelect={() => handleVehicleSelect(vehicle, line)}
        />
      )),
    [filteredVehicles, handleVehicleSelect, selectedVehicleId],
  );

  const stopAreaMarkers = useMemo(() => {
    if (!isAboveStopAreaZoom || !debouncedBounds) return undefined;

    const markers = stopAreas
      .filter((sa) => {
        if (sa.latitude === undefined || sa.longitude === undefined) return false;
        if (!debouncedBounds.contains([sa.latitude, sa.longitude])) return false;
        return true;
      })
      .map((stopArea) => <StopLocationMarker stopLocation={stopArea} key={stopArea.gid} />);

    return markers.length > 0 ? markers : undefined;
  }, [isAboveStopAreaZoom, stopAreas, debouncedBounds]);

  const mapMarkers = useMemo(() => {
    const markers = [...vehicleMarkers, ...(stopAreaMarkers ?? []), ...(userPosition ? [<UserLocationMarker key="user-location" position={userPosition} />] : [])];

    return markers.length > 0 ? markers : undefined;
  }, [stopAreaMarkers, vehicleMarkers, userPosition]);

  const selectedRouteStopPointMarkers = useMemo(() => {
    if (!debouncedBounds || selectedVehicleRouteStopPointGids.length === 0) return undefined;

    const selectedLine = selectedVehicleRouteContext?.line;
    const highlightedRadius = isAboveStopAreaZoom ? 9 : 7;
    const stopPointsByGid = new Map<string, IStopPoint>();

    stopAreas.forEach((stopArea) => {
      stopArea.stopPoints.forEach((stopPoint) => {
        if (!stopPointsByGid.has(stopPoint.gid)) stopPointsByGid.set(stopPoint.gid, stopPoint);
      });
    });

    const markers = selectedVehicleRouteStopPointGids
      .map((gid) => stopPointsByGid.get(gid))
      .filter((stopPoint): stopPoint is IStopPoint => !!stopPoint && stopPoint.latitude !== undefined && stopPoint.longitude !== undefined)
      .filter((stopPoint) => debouncedBounds.contains([stopPoint.latitude!, stopPoint.longitude!]))
      .map((stopPoint) => (
        <StopLocationMarker
          stopLocation={stopPoint}
          key={stopPoint.gid}
          highlighted
          backgroundColor={selectedLine?.backgroundColor ?? undefined}
          foregroundColor={selectedLine?.foregroundColor ?? undefined}
          highlightedRadius={highlightedRadius}
        />
      ));

    return markers.length > 0 ? markers : undefined;
  }, [debouncedBounds, isAboveStopAreaZoom, selectedVehicleRouteContext, selectedVehicleRouteStopPointGids, stopAreas]);

  const selectedVehicleRouteLayers = useMemo(() => {
    if (!selectedVehicleRouteContext?.line || !selectedVehicleRouteCoordinates || selectedVehicleRouteCoordinates.length === 0) return undefined;

    return [
      <VehicleRoutePolylines
        key={`${selectedVehicleRouteContext.vehicleId}-${selectedVehicleRouteCoordinates.length}`}
        coordinates={selectedVehicleRouteCoordinates}
        line={selectedVehicleRouteContext.line}
      />,
    ];
  }, [selectedVehicleRouteContext, selectedVehicleRouteCoordinates]);

  const mapLayers = useMemo(() => (selectedVehicleRouteLayers && selectedVehicleRouteLayers.length > 0 ? selectedVehicleRouteLayers : undefined), [selectedVehicleRouteLayers]);

  const extraControls = useMemo(() => {
    const controls = [
      <MapSettingsControl
        key="map-settings-control"
        className={`vehicle-positions-map__map-settings-control d-none ${showMapSettings ? '' : 'd-lg-block'}`}
        position="topleft"
        onClick={(event) => {
          filterTriggerRef.current = event.currentTarget as HTMLElement;
          setShowJourneyPanel(false);
          setShowMapSettings(true);
        }}
      />,
    ];

    if (settings.showFeedbackControl && !feedbackHasBeenShown) {
      controls.push(
        <FeedbackControl
          key="feedback-control"
          className="vehicle-positions-map__feedback-control"
          position="bottomleft"
          onClick={() => {
            document.dispatchEvent(new CustomEvent('show-vehicle-position-map-feedback-panel'));
            setShowMapSettings(false);
            setShowJourneyPanel(false);
            setFeedbackHasBeenShown(true);
          }}
        />,
      );
    }
    return controls.length > 0 ? controls : undefined;
  }, [showMapSettings, settings.showFeedbackControl, feedbackHasBeenShown]);

  // === USE EFFECTS ===
  useEffect(() => {
    if (showMapSettings) {
      const buttons = panelRef.current?.querySelectorAll<HTMLButtonElement>('button');
      const firstVisibleButton = Array.from(buttons ?? []).find((button) => button.getClientRects().length > 0);
      firstVisibleButton?.focus({ preventScroll: true });
      return;
    }

    const trigger = filterTriggerRef.current;
    filterTriggerRef.current = null;
    if (!showJourneyPanel) trigger?.focus({ preventScroll: true });
  }, [showMapSettings, showJourneyPanel]);

  // Defer loading stop areas until the user has zoomed in sufficiently, to avoid fetching a large amount of data on initial load.
  // Once stop areas have been loaded once, they are cached for the rest of the session to avoid repeat requests.
  useEffect(() => {
    const shouldLoadStopAreas = isAboveStopAreaZoom || hasSelectedVehicleStopPoints;
    if (!shouldLoadStopAreas || hasLoadedStopAreas || isLoadingStopAreasRef.current) return;

    const abortController = new AbortController();
    stopAreasAbortControllerRef.current = abortController;
    isLoadingStopAreasRef.current = true;

    const clearStopAreasRequest = () => {
      if (stopAreasAbortControllerRef.current === abortController) {
        stopAreasAbortControllerRef.current = null;
      }

      isLoadingStopAreasRef.current = false;
    };

    const loadStopAreas = async () => {
      try {
        const response = await fetch(STOP_AREAS_URL, {
          signal: abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) {
          LogService.logError(new Error(`Failed to fetch stop areas: ${response.status} ${response.statusText}`));
          return;
        }

        const result = (await response.json()) as StopAreaApiModel[];
        setStopAreasData(result);
        setHasLoadedStopAreas(true);
      } catch (error) {
        if (abortController.signal.aborted) return;
        LogService.logError(error instanceof Error ? error : new Error('Failed to fetch stop areas'));
      } finally {
        clearStopAreasRequest();
      }
    };

    void loadStopAreas();
  }, [hasLoadedStopAreas, isAboveStopAreaZoom, hasSelectedVehicleStopPoints]);

  // === EVENT HANDLERS ===
  const handleMapClick = useCallback(() => {
    abortCurrentPositionLookup();
    setSelectedVehicleId(undefined);
    setShowJourneyPanel(false);
    setSelectedVehicleRouteContext(undefined);
    setSelectedVehicleRouteCoordinates(undefined);
    setSelectedVehicleRouteStopPointGids([]);
  }, [abortCurrentPositionLookup]);

  const handleBoundsChange = useCallback((newBounds: L.LatLngBounds) => {
    setBounds((previousBounds) => {
      if (previousBounds && !previousBounds.equals(newBounds)) {
        userHasInteractedRef.current = true;
      }

      return newBounds;
    });
  }, []);

  const handleFilterChange = useCallback((modes: TransportMode[]) => {
    setSelectedModes(modes);
  }, []);

  const handleLinesChange = useCallback((lines: IMultiFacet[]) => {
    setSelectedLines(lines);
  }, []);

  const handleMapSettingsClose = useCallback(() => {
    setShowMapSettings(false);
  }, []);

  const handleJourneyPanelClose = useCallback(() => {
    setShowJourneyPanel(false);
  }, []);

  const handlePrognosisUpdate = useCallback(() => {
    setNow(Date.now());
  }, []);

  const handleZoomEnd = useCallback((zoom: number) => {
    const wasAbove = zoomRef.current >= STOP_AREA_MIN_ZOOM;
    zoomRef.current = zoom;
    const isAbove = zoom >= STOP_AREA_MIN_ZOOM;
    if (wasAbove !== isAbove) setIsAboveStopAreaZoom(isAbove);
  }, []);

  const handleLocateControlClick = useCallback(() => {
    AnalyticsService.addEvent(AnalyticsService.VehiclePositionsMap, 'Klick på min position');
  }, []);

  const updateSelectedVehicleJourney = useEffectEvent(() => {
    const vehicle = selectedVehicleId ? vehiclesRef.current.get(selectedVehicleId) : undefined;
    if (!vehicle || !selectedVehicleRouteContext || vehicle.serviceJourneyGid === selectedVehicleRouteContext.serviceJourneyGid) return;

    const lineGid = VasttrafikGidHelper.getLineGidFromServiceJourneyGid(vehicle.serviceJourneyGid);
    vehicleRouteAbortControllerRef.current?.abort();
    setSelectedVehicleRouteContext({
      ...selectedVehicleRouteContext,
      serviceJourneyGid: vehicle.serviceJourneyGid,
      line: (lineGid ? linesByGid.get(lineGid) : undefined) ?? selectedVehicleRouteContext.line,
      destination: `${vehicle.destination ?? ''}${vehicle.via ? ` via ${vehicle.via}` : ''}`,
    });
    setSelectedVehicleRouteCoordinates(undefined);
    setSelectedVehicleRouteStopPointGids([]);
  });

  // === EFFECTS ===
  useEffect(() => {
    if (!showJourneyPanel) return;
    const timer = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(timer);
  }, [showJourneyPanel]);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedBounds(bounds);
    }, BOUNDS_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [bounds]);

  useEffect(() => {
    if (!debouncedBounds) return;

    const url = new URL(settings.streamUrl);
    url.searchParams.set('north', debouncedBounds.getNorth().toString());
    url.searchParams.set('south', debouncedBounds.getSouth().toString());
    url.searchParams.set('east', debouncedBounds.getEast().toString());
    url.searchParams.set('west', debouncedBounds.getWest().toString());
    for (const gid of selectedLineGids) url.searchParams.append('lines', gid);

    const vehicleMap = vehiclesRef.current;
    vehicleMap.clear();

    const eventSource = new EventSource(url.toString());

    eventSource.addEventListener('snapshot', (event: MessageEvent<string>) => {
      const positions = JSON.parse(event.data) as VehiclePosition[];
      applySnapshot(vehicleMap, positions);
      updateSelectedVehicleJourney();
      setVehicles([...vehicleMap.values()]);
      setNow(Date.now());
    });

    eventSource.addEventListener('delta', (event: MessageEvent<string>) => {
      const delta = JSON.parse(event.data) as VehiclePositionsDelta;
      applyDelta(vehicleMap, delta);
      updateSelectedVehicleJourney();
      setVehicles([...vehicleMap.values()]);
      setNow(Date.now());
    });

    return () => {
      eventSource.close();
    };
  }, [debouncedBounds, settings.streamUrl, selectedLineGids]);

  useEffect(() => {
    vehicleRouteAbortControllerRef.current?.abort();

    if (!selectedServiceJourneyGid) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedVehicleRouteCoordinates(undefined);
      setSelectedVehicleRouteStopPointGids([]);
      return;
    }

    const abortController = new AbortController();
    vehicleRouteAbortControllerRef.current = abortController;

    const loadVehicleRoute = async () => {
      try {
        const url = new URL(settings.streamUrl);
        if (url.pathname.endsWith(STREAM_SUFFIX)) url.pathname = `${url.pathname.slice(0, -STREAM_SUFFIX.length)}/journey-pattern`;
        else url.pathname = `${url.pathname}/journey-pattern`;

        url.search = '';
        url.searchParams.set('serviceJourneyGid', selectedServiceJourneyGid);

        const response = await fetch(url, {
          signal: abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) {
          LogService.logError(new Error(`Failed to fetch vehicle route: ${response.status} ${response.statusText}`));
          setSelectedVehicleRouteCoordinates(undefined);
          setSelectedVehicleRouteStopPointGids([]);
          return;
        }

        const result = (await response.json()) as IJourneyPatternResponse;
        setSelectedVehicleRouteCoordinates(result.coordinates);
        setSelectedVehicleRouteStopPointGids(result.stopPointGids);
      } catch (error) {
        if (abortController.signal.aborted) return;
        LogService.logError(error instanceof Error ? error : new Error('Failed to fetch vehicle route'));
        setSelectedVehicleRouteCoordinates(undefined);
        setSelectedVehicleRouteStopPointGids([]);
      }
    };

    void loadVehicleRoute();

    return () => {
      if (vehicleRouteAbortControllerRef.current === abortController) vehicleRouteAbortControllerRef.current = null;
      abortController.abort();
    };
  }, [selectedServiceJourneyGid, settings.streamUrl]);

  return (
    <div className={`vehicle-positions-map ${showMapSettings ? 'vehicle-positions-map--show-map-settings' : ''} ${showJourneyPanel ? 'vehicle-positions-map--show-journey-panel' : ''}`}>
      <div className={`vehicle-positions-map__settings ${showMapSettings || showJourneyPanel ? 'd-none' : 'd-flex'} justify-content-center d-lg-none`}>
        <button
          type="button"
          className="btn btn-tertiary btn-icon-right btn-icon-chevron btn-tertiary-icon-rotatable"
          aria-expanded={showMapSettings}
          aria-controls={showMapSettings ? filterPanelId : undefined}
          onClick={(event) => {
            filterTriggerRef.current = event.currentTarget;
            setShowJourneyPanel(false);
            setShowMapSettings(!showMapSettings);
          }}
        >
          {t('Filter', 'Filter')}
          {hasActiveFilters && <span>&nbsp;({t('ändrade', 'changed')})</span>}
        </button>
      </div>
      {(showMapSettings || showJourneyPanel) && (
        <div ref={panelRef} className="vehicle-positions-map__panel">
          {showMapSettings && (
            <MapSettings
              id={filterPanelId}
              className="vehicle-positions-map__settings"
              selectedLines={selectedLines}
              selectedModes={selectedModes}
              onLinesChange={handleLinesChange}
              onTransportModesChange={handleFilterChange}
              informationTextHtml={texts.informationTextHtml}
              onClose={handleMapSettingsClose}
            />
          )}
          {showJourneyPanel && selectedVehicleRouteContext && (
            <JourneyPanel
              key={`${selectedVehicleRouteContext.vehicleId}-${selectedVehicleRouteContext.serviceJourneyGid}`}
              serviceJourneyGid={selectedVehicleRouteContext.serviceJourneyGid}
              streamUrl={settings.streamUrl}
              line={selectedVehicleRouteContext.line}
              destination={selectedVehicleRouteContext.destination}
              stopAreas={stopAreas}
              vehicle={vehicles.find((vehicle) => vehicle.vehicleId === selectedVehicleId)}
              now={now}
              onPrognosisUpdate={handlePrognosisUpdate}
              onClose={handleJourneyPanelClose}
            />
          )}
        </div>
      )}
      <MapComponent
        ref={mapRef}
        ariaLabel="Vehicle positions map"
        zoomLevel={13}
        onBoundsChange={handleBoundsChange}
        onZoomEnd={handleZoomEnd}
        markers={mapMarkers}
        extraMarkers={selectedRouteStopPointMarkers}
        layers={[
          <MapPanelLayout
            key="panel-layout"
            panelRef={panelRef}
            active={showMapSettings || showJourneyPanel}
            selectedVehicleId={selectedVehicleId}
            vehicle={vehicles.find((vehicle) => vehicle.vehicleId === selectedVehicleId)}
          />,
          ...(mapLayers ?? []),
        ]}
        extraControls={extraControls}
        drawLocationControlMarker={userPosition === undefined}
        onClick={handleMapClick}
        onLocateControlClick={handleLocateControlClick}
        onDragStart={abortCurrentPositionLookup}
      />
    </div>
  );
}
